import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Extension, textblockTypeInputRule, type Editor, type JSONContent } from '@tiptap/core';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Placeholder } from '@tiptap/extensions';
import Mention from '@tiptap/extension-mention';
import type { SuggestionKeyDownProps, SuggestionProps } from '@tiptap/suggestion';
import {
  AtSignIcon,
  BoldIcon,
  CodeIcon,
  ItalicIcon,
  Link2OffIcon,
  LinkIcon,
  ListIcon,
  ListOrderedIcon,
  SendHorizontalIcon,
  SquareCodeIcon,
  StrikethroughIcon,
  UnderlineIcon,
} from 'lucide-react';
import { cn } from '../cn';
import {
  isBlank,
  isSafeHref,
  type RichTextBlock,
  type RichTextDoc,
  type RichTextInline,
  type RichTextListItem,
  type RichTextMark,
} from '../rich-text/doc';
import { Button } from './primitives';

/* ────────────────────────────── Rendering ────────────────────────────── */

function withMarks(text: string, marks: readonly RichTextMark[]): React.ReactNode {
  return marks.reduce<React.ReactNode>((inner, mark) => {
    switch (mark.type) {
      case 'bold':
        return <b>{inner}</b>;
      case 'italic':
        return <i>{inner}</i>;
      case 'underline':
        return <u>{inner}</u>;
      case 'strike':
        return <s>{inner}</s>;
      case 'code':
        return <code>{inner}</code>;
      case 'link':
        // The API refuses anything but http(s) and mailto; checked again here
        // so a document from anywhere else still cannot run script.
        return isSafeHref(mark.attrs.href) ? (
          <a href={mark.attrs.href} target="_blank" rel="noreferrer noopener">
            {inner}
          </a>
        ) : (
          inner
        );
      default: {
        const never: never = mark;
        return never;
      }
    }
  }, text);
}

function Inline({ node }: { readonly node: RichTextInline }): React.ReactElement {
  switch (node.type) {
    case 'text':
      return <>{withMarks(node.text, node.marks ?? [])}</>;
    case 'hardBreak':
      return <br />;
    case 'mention':
      return <span className="portal-mention">@{node.attrs.label}</span>;
    default: {
      const never: never = node;
      return never;
    }
  }
}

function ListItems({ items }: { readonly items: readonly RichTextListItem[] }): React.ReactElement {
  return (
    <>
      {items.map((item, index) => (
        <li key={index}>
          {(item.content ?? []).map((child, childIndex) => (
            <Block key={childIndex} block={child} />
          ))}
        </li>
      ))}
    </>
  );
}

function Block({ block }: { readonly block: RichTextBlock }): React.ReactElement {
  switch (block.type) {
    case 'paragraph':
      return (
        <p>
          {(block.content ?? []).map((node, index) => (
            <Inline key={index} node={node} />
          ))}
        </p>
      );
    case 'bulletList':
      return (
        <ul>
          <ListItems items={block.content ?? []} />
        </ul>
      );
    case 'orderedList':
      return (
        <ol start={block.attrs?.start}>
          <ListItems items={block.content ?? []} />
        </ol>
      );
    case 'codeBlock':
      return (
        <pre>
          <code>{(block.content ?? []).map((node) => node.text).join('')}</code>
        </pre>
      );
    case 'heading': {
      const inline = (block.content ?? []).map((node, index) => <Inline key={index} node={node} />);
      return block.attrs.level === 2 ? <h2>{inline}</h2> : <h3>{inline}</h3>;
    }
    case 'blockquote':
      return (
        <blockquote>
          {(block.content ?? []).map((child, index) => (
            <Block key={index} block={child} />
          ))}
        </blockquote>
      );
    default: {
      const never: never = block;
      return never;
    }
  }
}

export interface RichTextProps {
  readonly doc: RichTextDoc;
  readonly className?: string;
}

/**
 * A rich-text document, rendered as React elements — never as HTML — so
 * nothing in it can run. Styled by `.portal-prose` in the theme.
 *
 * @param props - The document.
 * @returns The rendered text.
 */
export function RichText({ doc, className }: RichTextProps): React.ReactElement {
  return (
    <div className={cn('portal-prose', className)}>
      {(doc.content ?? []).map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </div>
  );
}

/* ─────────────────────────────── Editing ─────────────────────────────── */

/** Someone the editor can @-mention. */
export interface MentionCandidate {
  readonly id: string;
  readonly label: string;
}

export interface RichTextEditorProps {
  /** What the empty box invites. */
  readonly placeholder: string;
  readonly ariaLabel: string;
  /** People who can be tagged with `@`. Omit to turn mentions off. */
  readonly mentionCandidates?: readonly MentionCandidate[] | undefined;
  /** Called with the document on Enter, ⌘/Ctrl+Enter or the send button; the box then clears. */
  readonly onSubmit: (doc: RichTextDoc) => void;
  /** Greys the send button and the box, e.g. while a send is in flight. */
  readonly disabled?: boolean;
  readonly submitLabel?: string;
  readonly autoFocus?: boolean;
  readonly className?: string;
  /**
   * Text to start from — e.g. a mention of the person a "Message" button was
   * pressed on. Replaces what is in the box whenever `key` changes, and puts
   * the caret at the end.
   */
  readonly draft?: { readonly key: string; readonly doc: RichTextDoc } | undefined;
}

/** How many names the mention list offers at once. */
const SUGGESTION_LIMIT = 6;

/** The modifier key as this platform writes it, for shortcut hints. */
const MOD = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl+';

/** "Bold (⌘B)" — the label with its shortcut, the way Slack's tooltips read. */
function withKeys(label: string, keys: string): string {
  return `${label} (${MOD}${keys})`;
}

/** What the mention list needs from the suggestion plugin while it is open. */
interface SuggestionState {
  readonly items: readonly MentionCandidate[];
  readonly highlighted: number;
  readonly command: (item: MentionCandidate) => void;
}

/** An icon button on the formatting toolbar. */
function ToolButton({
  label,
  active = false,
  onClick,
  children,
}: {
  readonly label: string;
  readonly active?: boolean;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={active}
      // Before blur, so the editor keeps its selection for the command.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className={cn(
        'grid size-7 place-items-center rounded-portal-button',
        active ? 'bg-brand-tint text-brand' : 'text-portal-muted hover:bg-portal-sky hover:text-portal-ink',
      )}
    >
      {children}
    </button>
  );
}

/** A thin vertical rule between toolbar groups. */
function ToolDivider(): React.ReactElement {
  return <span aria-hidden className="mx-1 h-4 w-px bg-portal-line" />;
}

/** The address as typed, given a scheme when the writer left it off. */
function normaliseHref(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return trimmed;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return `mailto:${trimmed}`;
  return `https://${trimmed}`;
}

/**
 * The portal's message box, laid out the way people know from Slack: a
 * formatting bar above the text, `@` to tag someone, Enter to send and
 * Shift+Enter for a new line. What it hands back is a {@link RichTextDoc},
 * never HTML.
 *
 * @param props - Placeholder, who can be mentioned, and the submit action.
 * @returns The editor.
 */
export function RichTextEditor({
  placeholder,
  ariaLabel,
  mentionCandidates,
  onSubmit,
  disabled = false,
  submitLabel = 'Send',
  autoFocus = false,
  className,
  draft,
}: RichTextEditorProps): React.ReactElement {
  const listId = useId();
  const [suggestion, setSuggestion] = useState<SuggestionState | null>(null);
  const [empty, setEmpty] = useState(true);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkHref, setLinkHref] = useState('');
  // Rendered state, once per update, so the toolbar reflects the selection.
  const [, setTick] = useState(0);

  // The editor is created once; everything it needs later reads from refs.
  const candidatesRef = useRef(mentionCandidates ?? []);
  candidatesRef.current = mentionCandidates ?? [];
  const suggestionRef = useRef<SuggestionState | null>(null);
  suggestionRef.current = suggestion;
  const submitRef = useRef<() => void>(() => undefined);
  const openLinkRef = useRef<() => void>(() => undefined);

  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: false,
        blockquote: false,
        horizontalRule: false,
        link: {
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: { target: '_blank', rel: 'noreferrer noopener' },
        },
      }),
      Placeholder.configure({ placeholder }),
      Mention.configure({
        HTMLAttributes: { class: 'portal-mention' },
        renderText: ({ node }) => `@${String(node.attrs['label'] ?? node.attrs['id'])}`,
        suggestion: {
          char: '@',
          allowSpaces: false,
          items: ({ query }) => {
            const needle = query.toLowerCase();
            return candidatesRef.current
              .filter((candidate) => candidate.label.toLowerCase().includes(needle))
              .slice(0, SUGGESTION_LIMIT);
          },
          render: () => ({
            onStart: (props: SuggestionProps<MentionCandidate, MentionCandidate>) =>
              setSuggestion({ items: props.items, highlighted: 0, command: props.command }),
            onUpdate: (props: SuggestionProps<MentionCandidate, MentionCandidate>) =>
              setSuggestion({ items: props.items, highlighted: 0, command: props.command }),
            onExit: () => setSuggestion(null),
            onKeyDown: ({ event }: SuggestionKeyDownProps): boolean => {
              const current = suggestionRef.current;
              if (!current || current.items.length === 0) return false;
              const count = current.items.length;
              if (event.key === 'ArrowDown') {
                setSuggestion({ ...current, highlighted: (current.highlighted + 1) % count });
                return true;
              }
              if (event.key === 'ArrowUp') {
                setSuggestion({ ...current, highlighted: (current.highlighted - 1 + count) % count });
                return true;
              }
              if (event.key === 'Enter' || event.key === 'Tab') {
                const chosen = current.items[current.highlighted];
                if (chosen) current.command(chosen);
                return true;
              }
              if (event.key === 'Escape') {
                setSuggestion(null);
                return true;
              }
              return false;
            },
          }),
        },
      }),
      Extension.create({
        name: 'slackHabits',
        // ``` on its own line starts a code block at once, the way Slack does;
        // TipTap's own rule waits for a space after it.
        addInputRules() {
          return [textblockTypeInputRule({ find: /^```$/, type: this.editor.schema.nodes['codeBlock']! })];
        },
        addKeyboardShortcuts() {
          return {
            // Slack's combinations, alongside TipTap's own (⌘B, ⌘I, ⌘U, ⌘E,
            // ⌘⇧7 numbered, ⌘⇧8 bulleted).
            'Mod-Shift-x': () => this.editor.chain().focus().toggleStrike().run(),
            'Mod-Shift-c': () => this.editor.chain().focus().toggleCode().run(),
            'Mod-Shift-Alt-c': () => this.editor.chain().focus().toggleCodeBlock().run(),
            'Mod-Shift-u': () => {
              openLinkRef.current();
              return true;
            },
            Enter: () => {
              // The mention list owns Enter while it is open; inside a list or
              // a code block Enter still makes a new line, as in Slack.
              if (suggestionRef.current) return false;
              if (
                this.editor.isActive('bulletList') ||
                this.editor.isActive('orderedList') ||
                this.editor.isActive('codeBlock')
              ) {
                return false;
              }
              submitRef.current();
              return true;
            },
            'Mod-Enter': () => {
              submitRef.current();
              return true;
            },
          };
        },
      }),
    ],
    // Placeholder is read once; a changed placeholder means a different box.
    [placeholder],
  );

  const editor = useEditor(
    {
      extensions,
      autofocus: autoFocus,
      editorProps: {
        attributes: {
          class: 'portal-prose min-h-[44px] max-h-[40vh] overflow-y-auto px-3 py-2 text-portal-small focus:outline-none',
          'aria-label': ariaLabel,
          'aria-multiline': 'true',
          role: 'textbox',
        },
      },
      onUpdate: ({ editor: current }) => setEmpty(current.isEmpty),
      onSelectionUpdate: () => setTick((tick) => tick + 1),
      onTransaction: () => setTick((tick) => tick + 1),
    },
    [extensions],
  );

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  const draftKey = draft?.key;
  useEffect(() => {
    if (!editor || !draft) return;
    // TipTap wants a mutable document; a fresh copy is that.
    const content: JSONContent = JSON.parse(JSON.stringify(draft.doc));
    editor.commands.setContent(content);
    editor.commands.focus('end');
    setEmpty(editor.isEmpty);
    // Only a new draft (a new key) replaces the text; the doc object may be rebuilt on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor, draftKey]);

  submitRef.current = () => {
    if (!editor || disabled) return;
    const doc = editor.getJSON() as unknown as RichTextDoc;
    if (isBlank(doc)) return;
    onSubmit(doc);
    editor.commands.clearContent(true);
    setEmpty(true);
  };

  const openLink = (current: Editor): void => {
    const existing = current.getAttributes('link')['href'];
    setLinkHref(typeof existing === 'string' ? existing : '');
    setLinkOpen(true);
  };
  openLinkRef.current = () => {
    if (editor) openLink(editor);
  };

  const applyLink = (current: Editor): void => {
    const href = normaliseHref(linkHref);
    setLinkOpen(false);
    if (href.length === 0 || !isSafeHref(href)) {
      current.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    if (current.state.selection.empty && !current.isActive('link')) {
      // Nothing selected: write the address itself as the link.
      current
        .chain()
        .focus()
        .insertContent({ type: 'text', text: linkHref.trim(), marks: [{ type: 'link', attrs: { href } }] })
        .run();
      return;
    }
    current.chain().focus().extendMarkRange('link').setLink({ href }).run();
  };

  if (!editor) return <div className={cn('min-h-[120px]', className)} />;

  const tools: readonly ({ readonly divider: true } | {
    readonly label: string;
    readonly icon: React.ReactNode;
    readonly active: boolean;
    readonly run: () => void;
  })[] = [
    { label: withKeys('Bold', 'B'), icon: <BoldIcon aria-hidden className="size-4" />, active: editor.isActive('bold'), run: () => editor.chain().focus().toggleBold().run() },
    { label: withKeys('Italic', 'I'), icon: <ItalicIcon aria-hidden className="size-4" />, active: editor.isActive('italic'), run: () => editor.chain().focus().toggleItalic().run() },
    { label: withKeys('Underline', 'U'), icon: <UnderlineIcon aria-hidden className="size-4" />, active: editor.isActive('underline'), run: () => editor.chain().focus().toggleUnderline().run() },
    { label: withKeys('Strikethrough', '⇧X'), icon: <StrikethroughIcon aria-hidden className="size-4" />, active: editor.isActive('strike'), run: () => editor.chain().focus().toggleStrike().run() },
    { divider: true },
    { label: withKeys('Link', '⇧U'), icon: <LinkIcon aria-hidden className="size-4" />, active: editor.isActive('link') || linkOpen, run: () => (linkOpen ? setLinkOpen(false) : openLink(editor)) },
    { divider: true },
    { label: withKeys('Numbered list', '⇧7'), icon: <ListOrderedIcon aria-hidden className="size-4" />, active: editor.isActive('orderedList'), run: () => editor.chain().focus().toggleOrderedList().run() },
    { label: withKeys('Bulleted list', '⇧8'), icon: <ListIcon aria-hidden className="size-4" />, active: editor.isActive('bulletList'), run: () => editor.chain().focus().toggleBulletList().run() },
    { divider: true },
    { label: withKeys('Code', '⇧C'), icon: <CodeIcon aria-hidden className="size-4" />, active: editor.isActive('code'), run: () => editor.chain().focus().toggleCode().run() },
    { label: withKeys('Code block', '⇧⌥C'), icon: <SquareCodeIcon aria-hidden className="size-4" />, active: editor.isActive('codeBlock'), run: () => editor.chain().focus().toggleCodeBlock().run() },
  ];

  const canSend = !disabled && !empty;

  return (
    <div
      className={cn(
        'relative rounded-portal-input border border-[#cad3df] bg-white focus-within:border-brand',
        disabled ? 'opacity-70' : '',
        className,
      )}
    >
      <div
        role="toolbar"
        aria-label="Text formatting"
        className="flex flex-wrap items-center gap-0.5 border-b border-portal-line px-1.5 py-1"
      >
        {tools.map((tool, index) =>
          'divider' in tool ? (
            <ToolDivider key={index} />
          ) : (
            <ToolButton key={tool.label} label={tool.label} active={tool.active} onClick={tool.run}>
              {tool.icon}
            </ToolButton>
          ),
        )}
      </div>

      {linkOpen ? (
        <form
          className="flex items-center gap-2 border-b border-portal-line bg-portal-sky/40 px-2 py-1.5"
          onSubmit={(event) => {
            event.preventDefault();
            applyLink(editor);
          }}
        >
          <LinkIcon aria-hidden className="size-4 shrink-0 text-portal-muted" />
          <input
            type="text"
            inputMode="url"
            aria-label="Link address"
            placeholder="Paste or type a link"
            value={linkHref}
            autoFocus
            onChange={(event) => setLinkHref(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setLinkOpen(false);
                editor.commands.focus();
              }
            }}
            className="h-8 min-w-0 flex-1 rounded-portal-button border border-[#cad3df] px-2 text-portal-small focus:border-brand focus:outline-none"
          />
          <Button type="submit" size="compact">
            {editor.isActive('link') ? 'Update' : 'Add link'}
          </Button>
          {editor.isActive('link') ? (
            <ToolButton
              label="Remove link"
              onClick={() => {
                setLinkOpen(false);
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
              }}
            >
              <Link2OffIcon aria-hidden className="size-4" />
            </ToolButton>
          ) : null}
        </form>
      ) : null}

      <EditorContent editor={editor} aria-controls={suggestion ? listId : undefined} />

      <div className="flex items-center justify-between gap-2 px-1.5 pb-1.5">
        <div className="flex items-center gap-0.5">
          {mentionCandidates ? (
            <span data-guide="messages-tag" className="inline-flex">
              <ToolButton
                label="Tag someone"
                onClick={() => editor.chain().focus().insertContent('@').run()}
              >
                <AtSignIcon aria-hidden className="size-4" />
              </ToolButton>
            </span>
          ) : null}
        </div>
        <button
          type="button"
          data-guide="messages-send"
          aria-label={submitLabel}
          title={`${submitLabel} (Enter)`}
          disabled={!canSend}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => submitRef.current()}
          className={cn(
            'grid size-8 place-items-center rounded-portal-button transition-colors',
            canSend ? 'bg-brand text-white shadow-portal-button' : 'text-portal-muted',
          )}
        >
          <SendHorizontalIcon aria-hidden className="size-4" />
        </button>
      </div>

      {suggestion && suggestion.items.length > 0 ? (
        <ul
          id={listId}
          role="listbox"
          aria-label="People to tag"
          className="absolute right-0 bottom-full left-0 z-20 mb-1 max-h-60 overflow-auto rounded-portal-card border border-portal-line bg-white py-1 shadow-portal-card"
        >
          {suggestion.items.map((candidate, index) => (
            <li
              key={candidate.id}
              role="option"
              aria-selected={index === suggestion.highlighted}
              className={cn(
                'cursor-pointer px-3 py-2 text-portal-small',
                index === suggestion.highlighted ? 'bg-brand-tint text-brand' : 'hover:bg-portal-sky',
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                suggestion.command(candidate);
              }}
              onMouseEnter={() => setSuggestion({ ...suggestion, highlighted: index })}
            >
              <span className="font-bold">@{candidate.label}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
